import axios from "AxiosInstance";

const LS_KEY = "kuber-kai-settings-guide-v1";
const PREVIEW_KEY = "kuber-kai-settings-guide-preview";

export function readSettingsGuidePreview() {
  try {
    const q = new URLSearchParams(window.location.search).get("settingsGuide");
    if (q === "preview") localStorage.setItem(PREVIEW_KEY, "1");
    if (q === "off") {
      localStorage.removeItem(PREVIEW_KEY);
      return false;
    }
    return localStorage.getItem(PREVIEW_KEY) === "1";
  } catch {
    return false;
  }
}

export function enableSettingsGuidePreview() {
  saveSettingsGuideState({ dismissed: false });
  try {
    localStorage.setItem(PREVIEW_KEY, "1");
  } catch {
    /* ignore */
  }
}

export function disableSettingsGuidePreview() {
  try {
    localStorage.removeItem(PREVIEW_KEY);
  } catch {
    /* ignore */
  }
}

/** Mock empty store data so the guide UI can be tested on a filled account. */
export function getPreviewSettingsGuideData() {
  return {
    general: { storeName: "" },
    international: { sellingCountries: [], shippingCountries: [], currencies: [] },
    taxes: { hasRates: false },
    store: { billingAddress: [{}] },
  };
}

export function buildPreviewSettingsGuideStatus() {
  return getSettingsCompletionStatus(getPreviewSettingsGuideData());
}

export function loadSettingsGuideState() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

export function saveSettingsGuideState(patch) {
  try {
    const next = { ...loadSettingsGuideState(), ...patch };
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    return next;
  } catch {
    return patch;
  }
}

export const SETTINGS_GUIDE_SECTIONS = [
  {
    key: "general",
    label: "General",
    priority: "essential",
    prompt:
      "Add your **store name**, logo, and regional formats - everything else builds on this.",
    check: (data) => Boolean(data.general?.storeName?.trim()),
    missingHint: "Store name",
  },
  {
    key: "selling",
    label: "International",
    priority: "essential",
    prompt:
      "Set **where you sell**, **regions you ship to**, and your **currency** - all in International settings.",
    check: (data) => {
      const intl = data.international;
      if (!intl) return false;
      const hasSelling =
        Array.isArray(intl.sellingCountries) && intl.sellingCountries.length > 0;
      const hasRegions =
        Array.isArray(intl.shippingCountries) && intl.shippingCountries.length > 0;
      const hasCurrency =
        Array.isArray(intl.currencies) && intl.currencies.length > 0;
      return hasSelling && hasRegions && hasCurrency;
    },
    missingHint: "Markets & currency",
  },
  {
    key: "taxes",
    label: "Taxes",
    priority: "essential",
    prompt:
      "Set up **tax classes and rates** so checkout totals are accurate for your market.",
    check: (data) => Boolean(data.taxes?.hasRates),
    missingHint: "Tax rates",
  },
  {
    key: "store",
    label: "Your store",
    priority: "recommended",
    prompt:
      "Add your **business address and contact details** - shoppers and invoices use these.",
    check: (data) => {
      const addr = data.store?.billingAddress?.[0];
      return Boolean(addr?.line1?.trim() || data.store?.businessEmail?.trim());
    },
    missingHint: "Store contact",
  },
  {
    key: "emailnotifications",
    label: "Emails",
    priority: "recommended",
    prompt: "Review **email templates** for orders and customer notifications.",
    check: () => null,
    missingHint: "Email templates",
  },
  {
    key: "product",
    label: "Products",
    priority: "recommended",
    prompt: "Set **catalog defaults** - SKU format, inventory rules, and display options.",
    check: () => null,
    missingHint: "Product defaults",
  },
  {
    key: "orders",
    label: "Orders",
    priority: "recommended",
    prompt: "Configure **order numbering** and checkout behaviour.",
    check: () => null,
    missingHint: "Order settings",
  },
];

function hasConfiguredTaxRates(taxClasses = []) {
  return taxClasses.some((tc) => {
    const defaultRate = Number(tc?.defaultTaxRate);
    if (!Number.isNaN(defaultRate) && defaultRate > 0) return true;
    return (tc?.taxRates || []).some((row) => {
      const rate = Number(row?.rate ?? row?.taxRate ?? row?.value);
      return !Number.isNaN(rate) && rate > 0;
    });
  });
}

export async function fetchSettingsGuideData() {
  if (readSettingsGuidePreview()) {
    return getPreviewSettingsGuideData();
  }

  const sessionId = localStorage.getItem("sessionId");
  if (!sessionId) return null;

  const headers = { sessionId };
  const sessionHeaders = { SessionId: sessionId };

  const [generalRes, internationalRes, taxClassRes, storeRes] = await Promise.all([
      axios.get("Merchant/GeneralSettings", { headers }).catch(() => null),
      axios
        .get("Merchant/internationalSelling", { headers: sessionHeaders })
        .catch(() => null),
      axios.get("TaxClass", { headers }).catch(() => null),
      axios
        .get("Merchant/storeSettings", { headers: sessionHeaders })
        .catch(() => null),
    ]);

  const taxClasses = taxClassRes?.data || [];

  return {
    general: generalRes?.data || null,
    international: internationalRes?.data || null,
    taxes: { hasRates: hasConfiguredTaxRates(taxClasses) },
    store: storeRes?.data || null,
  };
}

export function getSettingsCompletionStatus(data = {}) {
  const sections = SETTINGS_GUIDE_SECTIONS.map((def) => {
    const result = def.check(data);
    const done = result === true;
    const unknown = result === null;
    return {
      ...def,
      done: unknown ? false : done,
      unknown,
      needsAttention: !unknown && !done,
    };
  });

  const trackable = sections.filter((s) => !s.unknown);
  const essentialMissing = sections.filter(
    (s) => s.priority === "essential" && s.needsAttention
  );
  const recommendedMissing = sections.filter(
    (s) => s.priority === "recommended" && s.needsAttention
  );
  const incomplete = sections.filter((s) => s.needsAttention);
  const done = trackable.filter((s) => s.done).length;
  const total = trackable.length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const isEssentialComplete = essentialMissing.length === 0;
  const nextSection = essentialMissing[0] || recommendedMissing[0] || null;

  return {
    sections,
    essentialMissing,
    recommendedMissing,
    incomplete,
    nextSection,
    isEssentialComplete,
    isComplete: incomplete.length === 0,
    percent,
    done,
    total,
  };
}

export function buildSettingsGuidanceIntro(status) {
  if (!status) {
    return "Hi - I'm KAI. I'll help you work through your store settings step by step.";
  }

  if (status.isEssentialComplete) {
    const also = status.recommendedMissing.length;
    if (also === 0) {
      return "Your core settings look good. Explore any section below whenever you want to fine-tune things.";
    }
    return `Nice - **General**, **International**, and **Taxes** are in place.

When you're ready, I recommend finishing **${status.recommendedMissing
      .slice(0, 3)
      .map((s) => s.label)
      .join(", ")}**${also > 3 ? ", and more" : ""}. Tap a chip above to jump there.`;
  }

  const names = status.essentialMissing.map((s) => s.label).join(", ");
  const first = status.essentialMissing[0];

  return `Welcome to Settings. Before anything else, let's set up the essentials:

**${names}**

These three unlock the rest of your store. Tap a section above, or start with **${first.label}** below.`;
}
